// Editable confirm list for the chosen candidate's extracted fields.
// companyName must be non-empty to continue (server requires it at commit).
//
// String fields get text inputs. Structured values (address /
// socialMediaUrls / additionalContacts) have no sensible text input yet, so
// they render read-only with EnrichedReview's formatting rather than being
// dropped — the user still sees everything turn-1 read, and the values ride
// through to commit untouched.
import type { ExtractionCandidate } from '@stash/shared-types';
import { valueLines } from './fieldValueFormat';

interface FieldConfirmListProps {
  candidate: ExtractionCandidate;
  edits: Record<string, unknown>;
  onEdit: (field: string, value: unknown) => void;
  onContinue: () => void;
}

export function FieldConfirmList({ candidate, edits, onEdit, onContinue }: FieldConfirmListProps): React.JSX.Element {
  const effective = (field: string, fallback: unknown): unknown =>
    field in edits ? edits[field] : fallback;

  const companyName = effective(
    'companyName',
    candidate.fields.find((entry) => entry.field === 'companyName')?.value ?? '');
  const canContinue = typeof companyName === 'string' && companyName.trim().length > 0;

  return (
    <div className="flex flex-col gap-3 py-4">
      {candidate.fields.map((entry) => {
        const value = effective(entry.field, entry.value);
        if (typeof value === 'string') {
          return (
            <label key={entry.field} className="flex flex-col gap-1 text-sm font-medium text-muted-foreground">
              {entry.field}
              <input
                className="rounded-xl border border-input bg-card px-3 py-2 text-foreground focus-visible:outline-2 focus-visible:outline-ring"
                value={value}
                onChange={(event) => onEdit(entry.field, event.target.value)}
              />
            </label>
          );
        }

        const lines = valueLines(entry.field, value);
        if (lines.length === 0) return null;
        return (
          <div key={entry.field} className="rounded-xl border border-border bg-card px-3 py-2">
            <p className="m-0 flex items-center justify-between text-sm font-medium text-muted-foreground">
              {entry.field}
              <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                read-only
              </span>
            </p>
            <p className="m-0 text-sm font-semibold text-foreground">
              {lines.map((line) => (
                <span key={line} className="block break-words">{line}</span>
              ))}
            </p>
          </div>
        );
      })}
      <p className="m-0 text-xs text-muted-foreground">
        Next: we&rsquo;ll expand these details by visiting the business&rsquo;s website and
        checking other sources.
      </p>
      <button
        type="button"
        disabled={!canContinue}
        onClick={onContinue}
        className="mt-2 self-end rounded-full bg-primary px-6 py-2.5 font-semibold text-primary-foreground disabled:opacity-50"
      >
        Continue
      </button>
    </div>
  );
}
